import * as React from 'react'
import { useParams } from 'react-router-dom'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { DriverCalendarModel } from '@/app/models/calendar/driverCalendarModel'
import useCalendarService from '@/modules/drivers/services/CalendarService'
import YearlyActivityCalendar from '../components/charts/yearly/YearlyActivityCalendar'
import FlexView from '@/modules/shared/components/FlexView'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  getLeaveSummary,
  mergeLeaveSummaryWithApi,
} from '../utils/leaveSummary'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'

const Yearly = ({ showViolations,showTrips }: { showViolations?: boolean,showTrips : boolean }) => {
  const {
    companyId,
    driverId,
    year: currentYear,
  } = useParams<{
    companyId: string
    driverId: string
    year: string
  }>()
  const { setYear, year } = useDate()

  const { setType, refreshTrigger, setLeaveSummary } = useActivity()
  const { getCalendar } = useCalendarService()
  const { getDriverLeaveSummary } = useTimesheetService()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { t } = useLocalize()

  const [calendarData, setCalendarData] =
    React.useState<DriverCalendarModel | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  const fetchCalendar = async () => {
    const stopLoading = startLoading()
    setIsLoading(true)
    try {
      const yearNum = parseInt(currentYear, 10)
   
      const [data, apiLeaveSummary] = await Promise.all([
        getCalendar({ companyId, driverId, year: yearNum }),
        getDriverLeaveSummary({ companyId, driverId, year: yearNum }),
      ])
      setCalendarData(data)
      setLeaveSummary(
        mergeLeaveSummaryWithApi(getLeaveSummary(data), apiLeaveSummary),
      )
    } catch (error) {
      console.error('Error fetching calendar:', error)
    } finally {
      setIsLoading(false)
      stopLoading()
    }
  }

  React.useEffect(() => {
    if (year !== currentYear) {
      setYear(currentYear)
    }
    fetchCalendar()
  }, [currentYear, driverId])

  React.useEffect(() => {
    setType('yearly')
    return () => setLeaveSummary(null)
  }, [])

  React.useEffect(() => {
    if (refreshTrigger > 0) {
      fetchCalendar()
    }
  }, [refreshTrigger])

  return (
    <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-overflow-x-auto">
      <div className="tw-relative">
        {isLoading || calendarData ? (
          <YearlyActivityCalendar
            showViolations={showViolations}
            calendar={calendarData}
            companyId={companyId}
            driverId={driverId}
            showTrips={showTrips}
            isLoading={isLoading}
            refreshCalendar={fetchCalendar}
          />
        ) : (
          <div className="tw-grid tw-h-[500px] tw-flex-1 tw-place-items-center tw-p-4">
            <FlexView vAlignContent="center" hAlignContent="center">
              <span style={{ color: '#97A2B3' }} className="text-xl tw-my-4">
                {t('driverCalendar.no-data')}
              </span>
            </FlexView>
          </div>
        )}
      </div>
    </div>
  )
}

export default Yearly
